import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Select, Spin, message } from 'antd';
import { API_ORIGIN } from '../../../config';
import { firstName, refreshApprovers } from '../../../utils/approverStore';
import { formatEuro } from '../../../utils/directOrderStatus';

const BASE = `${API_ORIGIN}/api/direct-orders/approvers`;

/** Signed-in user has the Admin role (3), primary or additional: who may change approvers. */
export const isSystemAdmin = (user) =>
  [user?.role, ...(user?.additionalRoles || [])].map((r) => Number(r)).includes(3);

const request = async (path, options = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) throw new Error(data.error || 'Something went wrong. Try again.');
  return data;
};

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const waitingText = ({ orders, tickets }) => {
  if (!orders && !tickets) return 'Nothing waiting';
  const parts = [];
  if (orders) parts.push(plural(orders, 'order', 'orders'));
  if (tickets) parts.push(plural(tickets, 'ticket', 'tickets'));
  return `${parts.join(' and ')} waiting`;
};

const itemTitle = (item) =>
  item.subject === 'order'
    ? `Order ${item.doRef}`
    : `Ticket #${item.id}: ${item.ticketType === 'delete' ? 'delete' : 'change'} order ${item.doRef}`;

/** The orders and tickets the new approver can be emailed about; all ticked to start with. */
const HandoverModal = ({ handover, onClose, onSent }) => {
  const [chosen, setChosen] = useState([]);
  const [sending, setSending] = useState(false);
  const key = (item) => `${item.subject}:${item.id}`;

  useEffect(() => {
    setChosen(handover ? handover.items.map(key) : []);
  }, [handover]);

  if (!handover) return null;
  const { level, approver, items } = handover;
  const name = firstName(approver) || approver.name;

  const send = async () => {
    setSending(true);
    try {
      const picked = items.filter((item) => chosen.includes(key(item))).map((item) => ({ subject: item.subject, id: item.id }));
      const result = await request(`/${level}/handover`, { method: 'POST', body: JSON.stringify({ items: picked }) });
      if (result.failed?.length) {
        message.warning(`Sent ${result.sent}. Couldn't send ${result.failed.join(', ')}; try those again.`, 6);
      } else {
        message.success(`Sent ${plural(result.sent, 'email', 'emails')} to ${name}`);
      }
      onSent();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const allChosen = chosen.length === items.length;

  return (
    <Modal
      title={`Email ${name} about what's waiting?`}
      open
      onCancel={onClose}
      rootClassName="do-root do-scope"
      width={560}
      footer={[
        <Button key="later" onClick={onClose}>
          Not now
        </Button>,
        <Button key="send" type="primary" loading={sending} disabled={chosen.length === 0} onClick={send}>
          Send to {name}
        </Button>,
      ]}
    >
      <p className="do-muted" style={{ margin: '0 0 12px' }}>
        These were waiting at this level when {approver.name} took over. Each one you tick gets the usual approval email
        with Approve and Reject buttons. If you choose Not now, you can send them later from this page.
      </p>
      <div className="do-handover__all">
        <Checkbox
          checked={allChosen}
          indeterminate={chosen.length > 0 && !allChosen}
          onChange={(e) => setChosen(e.target.checked ? items.map(key) : [])}
        >
          {allChosen ? `All ${items.length} selected` : `${chosen.length} of ${items.length} selected`}
        </Checkbox>
      </div>
      <Checkbox.Group value={chosen} onChange={setChosen} className="do-handover">
        {items.map((item) => (
          <Checkbox key={key(item)} value={key(item)} className="do-handover__item">
            <span className="do-handover__title">{itemTitle(item)}</span>
            <span className="do-handover__meta">
              {item.supplier || 'No supplier'}
              {item.totalCost ? <span className="do-num">{formatEuro(item.totalCost)}</span> : null}
            </span>
          </Checkbox>
        ))}
      </Checkbox.Group>
    </Modal>
  );
};

/** Pick a person (and optionally a position) for a level. */
const ChangeModal = ({ level, people, levels, onClose, onSaved }) => {
  const current = level?.current;
  const [email, setEmail] = useState(null);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setEmail(current?.email || null);
    setTitle(current?.title || '');
    setError(null);
  }, [level, current]);

  const heldElsewhere = useMemo(() => {
    const map = {};
    (levels || []).forEach((l) => {
      if (l.current && l.level !== level?.level) map[l.current.email.toLowerCase()] = l.label;
    });
    return map;
  }, [levels, level]);

  if (!level) return null;

  const samePerson = email && current && email === current.email.toLowerCase();
  const nothingChanged = samePerson && (title.trim() || null) === (current.title || null);
  const waiting = level.waiting.orders + level.waiting.tickets;

  const options = people.map((p) => ({
    value: p.email,
    label: heldElsewhere[p.email] ? `${p.name} (holds the ${heldElsewhere[p.email].toLowerCase()})` : p.name,
    search: `${p.name} ${p.email}`.toLowerCase(),
    disabled: Boolean(heldElsewhere[p.email]),
  }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await request(`/${level.level}`, {
        method: 'PUT',
        body: JSON.stringify({ email, title: title.trim() || null }),
      });
      onSaved(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Change the ${level.label.toLowerCase()}`}
      open
      onCancel={onClose}
      onOk={save}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={saving}
      okButtonProps={{ disabled: !email || nothingChanged }}
      rootClassName="do-root do-scope"
      width={480}
    >
      <div className="do-setting__fields" style={{ display: 'grid', gap: 16 }}>
        <div className="do-setting__field">
          <label htmlFor="do-approver-person">Approver</label>
          <Select
            id="do-approver-person"
            showSearch
            value={email}
            onChange={setEmail}
            options={options}
            filterOption={(input, option) => option.search.includes(input.toLowerCase())}
            placeholder="Search by name or email"
            style={{ width: '100%' }}
          />
          <p className="do-panel__hint">Only people with a Festivals Portal login are listed: approvers sign in to approve.</p>
        </div>
        <div className="do-setting__field">
          <label htmlFor="do-approver-title">Position (optional)</label>
          <Input
            id="do-approver-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            placeholder="e.g. CEO"
          />
          <p className="do-panel__hint">Shown next to their name in emails and on the order PDF.</p>
        </div>
      </div>

      {email && !samePerson && (
        <Alert
          style={{ marginTop: 16 }}
          type={waiting ? 'warning' : 'info'}
          showIcon
          message={
            waiting
              ? `${waitingText(level.waiting)} at this level. They'll move to the new approver, and you can choose which to email them about after saving.`
              : 'Nothing is waiting at this level, so no emails need to go out.'
          }
          description={current ? `${current.name}'s past decisions stay on record under their name.` : undefined}
        />
      )}
      {samePerson && !nothingChanged && (
        <p className="do-muted" style={{ margin: '16px 0 0' }}>
          Only the position changes. Decisions already made keep the position they were made under.
        </p>
      )}
      {error && <Alert style={{ marginTop: 16 }} type="error" showIcon message={error} />}
    </Modal>
  );
};

/**
 * Admins choose who approves direct orders at each level. A level always has someone; changing it
 * replaces them, keeps their history, and offers to email the newcomer what was waiting.
 */
const ApproversPanel = () => {
  const [levels, setLevels] = useState(null);
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [handover, setHandover] = useState(null);
  const [openHistory, setOpenHistory] = useState({});

  const load = useCallback(async () => {
    try {
      const data = await request('/manage');
      setLevels(data.levels);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
    request('/candidates')
      .then((data) => setPeople(data.people || []))
      .catch(() => setPeople([]));
  }, [load]);

  const onSaved = (result) => {
    const level = editing;
    setEditing(null);
    refreshApprovers();
    load();
    if (result.changed === 'none') return;
    if (result.changed === 'position') {
      message.success(`Position updated for ${result.approver.name}`);
      return;
    }
    message.success(`${result.approver.name} now gives the ${level.label.toLowerCase()}`);
    if (result.handover?.length) setHandover({ level: level.level, approver: result.approver, items: result.handover });
  };

  if (loadError) return <Alert type="error" showIcon message="Couldn't load the approvers" description={loadError} />;
  if (!levels) return <div className="do-empty"><Spin /></div>;

  return (
    <>
      <ol className="do-approvers">
        {levels.map((level) => {
          const { current } = level;
          const past = level.history.filter((h) => h.unassigned_at);
          const historyOpen = openHistory[level.level];
          return (
            <li key={level.level} className="do-approver">
              <span className="do-approver__step" aria-hidden="true">{level.level}</span>
              <div className="do-approver__body">
                <div className="do-approver__head">
                  <div className="do-approver__who">
                    <span className="do-approver__level">{level.label}</span>
                    <h2 className="do-approver__name">{current ? current.name : 'Nobody assigned'}</h2>
                    <p className="do-approver__meta">
                      <span className={current?.title ? 'do-approver__title' : 'do-approver__unset'}>
                        {current?.title || 'Position not set'}
                      </span>
                      {current && <span>{current.email}</span>}
                      {current && <span>Since {fmtDate(current.assigned_at)}</span>}
                    </p>
                  </div>
                  <div className="do-approver__side">
                    <span className={`do-approver__waiting${level.waiting.orders + level.waiting.tickets ? ' is-busy' : ''}`}>
                      {waitingText(level.waiting)}
                    </span>
                    <Button onClick={() => setEditing(level)}>Change</Button>
                  </div>
                </div>

                {level.unsent.length > 0 && current && (
                  <div className="do-approver__notice" role="status">
                    <span>
                      {current.name} hasn't been emailed about {plural(level.unsent.length, 'item', 'items')} that were waiting when
                      they took over.
                    </span>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => setHandover({ level: level.level, approver: current, items: level.unsent })}
                    >
                      Review and send
                    </Button>
                  </div>
                )}

                {past.length > 0 && (
                  <div className="do-approver__history">
                    <button
                      type="button"
                      className="do-link"
                      aria-expanded={Boolean(historyOpen)}
                      onClick={() => setOpenHistory((s) => ({ ...s, [level.level]: !s[level.level] }))}
                    >
                      {historyOpen ? 'Hide' : 'Show'} previous approvers ({past.length})
                    </button>
                    {historyOpen && (
                      <ul className="do-approver__past">
                        {past.map((h) => (
                          <li key={h.id}>
                            <span className="do-approver__past-name">
                              {h.name}
                              {h.title ? `, ${h.title}` : ''}
                            </span>
                            <span className="do-muted do-num">
                              {fmtDate(h.assigned_at)} to {fmtDate(h.unassigned_at)}
                              {h.unassigned_by ? `, changed by ${h.unassigned_by}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <p className="do-panel__hint" style={{ marginTop: 16 }}>
        Orders go through the levels in order. Changes take effect straight away; decisions already made keep the name and
        position of whoever made them.
      </p>

      <ChangeModal level={editing} levels={levels} people={people} onClose={() => setEditing(null)} onSaved={onSaved} />
      <HandoverModal
        handover={handover}
        onClose={() => setHandover(null)}
        onSent={() => {
          setHandover(null);
          load();
        }}
      />
    </>
  );
};

export default ApproversPanel;
